import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useUser, useOrganizationList } from '@clerk/clerk-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useBranding } from '../App/hooks/useBranding';
import { useCompleteOnboarding } from './hooks';
import WizardProgressBar from './components/WizardProgressBar';
import WizardStep1 from './components/WizardStep1';
import WizardStep2 from './components/WizardStep2';
import WizardStep3 from './components/WizardStep3';
import CalendlyBookingStep from './components/CalendlyBookingStep';
import OperatorOnboardingPdfStep from './components/OperatorOnboardingPdfStep';
import ErrorMessage from './components/ErrorMessage';
import CgConfirmDialog from 'modules/Common/ui/CgConfirmDialog';
import logo from 'assets/icons/logo.png';
import { validateStep1, validateStep3 } from './utils/validation';
import {
  trackWizardCompletion,
  identifyUser,
  trackFreeSupplierOnboardingCompleted,
} from 'helpers/mixpanel';
import { getFreeTierInvite, hasFreeTierInvite } from 'modules/Onboarding/utils/freeTierInvite';
import { useTranslation } from 'react-i18next';
import { FREE_TIER_SUPPLIER_PLAN, LITE_HOME } from 'modules/Common/permissions/planGating';
import { apiErrorMessage, issuesOf, renderIssue } from 'helpers/serverIssues';

// Org types that see the optional Calendly onboarding-call step after the
// wizard. Consultants skip straight to their dashboard.
const CALENDLY_ELIGIBLE_TYPES = ['operator', 'declarant'];

// The Calendly booking step and the operator PDF brochure are CG-specific
// onboarding content — whitelabel and partner-embed hosts skip them.
// Whitelabel builds are excluded by env var regardless of hostname, since
// localhost would otherwise pass the host check during whitelabel dev.
const isCarbonGlanceHost = () => {
  if ((process.env.REACT_APP_WHITELABEL || 'carbonglance') !== 'carbonglance') return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  return (
    host === 'carbonglance.com' ||
    host.endsWith('.carbonglance.com') ||
    host === 'localhost' ||
    host === '127.0.0.1'
  );
};

const buildStepLabels = (t) => [
  t('onboarding:steps.company', 'Company'),
  t('onboarding:steps.useCase', 'Use case'),
  t('onboarding:steps.background', 'Background'),
];

/**
 * OnboardingWizard — multi-step onboarding wrapped in a Radix-inspired
 * card. The shell mirrors CgDialog's visual language (rounded-2xl,
 * slate-100 dividers, soft shadow) so the experience feels native to the
 * rest of the modernized app.
 */
const OnboardingWizard = () => {
  const { t } = useTranslation('onboarding');
  const history = useHistory();
  const location = useLocation();
  const brand = useBranding();
  const { user } = useUser();
  const { setActive } = useOrganizationList({
    userMemberships: { pageSize: 10 },
  });

  const organizationType = new URLSearchParams(location.search).get('type');

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    organizationName: '',
    organizationSize: '',
    industrySector: '',
    primaryUseCase: '',
    complianceTimeline: '',
    hasUsedCbamSoftwareBefore: '',
    annualVolume: '',
    userJobRole: '',
    otherJobRole: '',
    hearAboutUs: '',
    otherHearAboutUs: '',
  });
  const [skippedSteps, setSkippedSteps] = useState([]);
  const [errors, setErrors] = useState({});
  const [isFinishing, setIsFinishing] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [showCalendlyStep, setShowCalendlyStep] = useState(false);
  const [showOperatorPdfStep, setShowOperatorPdfStep] = useState(false);
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState(null);
  const [pendingOrganizationId, setPendingOrganizationId] = useState(null);

  const activateOrgAndRedirect = async (redirectUrl) => {
    if (pendingOrganizationId && setActive) {
      try {
        await setActive({ organization: pendingOrganizationId });
      } catch (e) {
        console.error('Failed to set active organization after onboarding:', e);
      }
    }
    history.push(redirectUrl || getRedirectUrl(organizationType));
  };

  const getRedirectUrl = (orgType) => {
    // Free-tier suppliers (operator-type orgs on the restricted plan) land in
    // the lite journey, not the full operator app.
    if (hasFreeTierInvite()) {
      return LITE_HOME;
    }
    if (orgType === 'declarant') {
      return '/overview';
    } else if (orgType === 'consultant') {
      return '/consultant-operators';
    } else if (orgType === 'operator') {
      return '/operator-installations';
    }
    return '/overview';
  };

  const {
    mutate: completeOnboarding,
    isLoading,
    isPending,
    isFetching,
  } = useCompleteOnboarding({
    onSuccess: async (data) => {
      setIsFinishing(true);

      if (user && data?.organization) {
        identifyUser(user, data.organization);
      }

      trackWizardCompletion({
        organizationType,
        organizationName: formData.organizationName,
        organizationId: data?.organization?.id,
        skippedSteps,
        user,
      });

      try {
        const newOrgId = data?.organization?.id;

        // Free-tier suppliers skip the CG onboarding-call / PDF upsell steps and
        // go straight into /lite. Prefer the backend-stamped plan (robust across
        // devices — localStorage may be absent if signup finished elsewhere);
        // fall back to the same-device invite hint.
        const isFreeTier =
          data?.organization?.publicMetadata?.plan === FREE_TIER_SUPPLIER_PLAN ||
          hasFreeTierInvite();

        // Free-tier-supplier funnel: onboarding completed (in addition to the
        // generic FreeT wizard-completion event above).
        if (isFreeTier) {
          const invite = getFreeTierInvite();
          trackFreeSupplierOnboardingCompleted({
            declarantName: invite?.declarantName,
            entry: invite?.token ? 'declarant' : 'self-serve',
          });
        }

        const redirectUrl = isFreeTier ? LITE_HOME : getRedirectUrl(organizationType);

        if (
          !isFreeTier &&
          CALENDLY_ELIGIBLE_TYPES.includes(organizationType) &&
          isCarbonGlanceHost()
        ) {
          // Defer setActive until the user dismisses the Calendly step.
          // Switching the active org now triggers BackgroundJobsScope to wrap
          // its children in a Provider for operator/consultant orgs, which
          // remounts this component and wipes the showCalendlyStep state
          // before it can render.
          setPendingOrganizationId(newOrgId);
          setPendingRedirectUrl(redirectUrl);
          setShowCalendlyStep(true);
        } else {
          if (newOrgId && setActive) {
            try {
              await setActive({ organization: newOrgId });
            } catch (e) {
              console.error('Failed to set active organization after onboarding:', e);
            }
          }
          history.push(redirectUrl);
        }
      } finally {
        setIsFinishing(false);
      }
    },
    onError: (error) => {
      if (error.response?.status === 409) {
        const redirectUrl = getRedirectUrl(organizationType);
        setTimeout(() => {
          history.push(redirectUrl);
        }, 2000);
      } else if (error.response?.status === 400) {
        // Machine code, never the English prose (D12) — the message is a
        // render fallback, not an API. Older backends without issues[] send
        // the same failure as this exact sentence, so keep that as fallback.
        const alreadyHasOrg =
          error.response?.data?.code === 'ORG_ALREADY_EXISTS' ||
          issuesOf(error).some((i) => i.code === 'ORGANIZATION_ALREADY_EXISTS') ||
          error.response.data?.message ===
            'User already has an organization. Cannot create duplicate.' ||
          error.response.data?.message === 'User already has an organization';
        if (alreadyHasOrg) {
          history.push('/init');
        }
        // Per-field first: the onboarding payload's field names are
        // byte-identical to the form state's, so path.at(-1) maps directly.
        const fieldErrors = {};
        const unmatched = [];
        for (const issue of issuesOf(error)) {
          const field =
            Array.isArray(issue.path) && issue.path.length > 0 ? String(issue.path.at(-1)) : null;
          if (field && Object.prototype.hasOwnProperty.call(formData, field)) {
            fieldErrors[field] = renderIssue(issue);
          } else {
            unmatched.push(issue);
          }
        }
        if (unmatched.length > 0 || Object.keys(fieldErrors).length === 0) {
          fieldErrors.submit =
            apiErrorMessage(error) ||
            t('errors.validation', 'Validation error. Please check your inputs.');
        }
        setErrors(fieldErrors);
      } else {
        setErrors({ submit: t('errors.generic', 'Something went wrong. Please try again.') });
      }
    },
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleNext = () => {
    let validationErrors = {};

    if (currentStep === 1) {
      validationErrors = validateStep1(formData);
    } else if (currentStep === 3) {
      validationErrors = validateStep3(formData);
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep((prev) => prev - 1);
  };

  const handleSkip = () => {
    const newSkippedSteps = [...skippedSteps, currentStep];
    setSkippedSteps(newSkippedSteps);

    if (currentStep === 3) {
      handleSubmit(newSkippedSteps);
    } else {
      setErrors({});
      setCurrentStep((prev) => prev + 1);
    }
  };

  const buildPayload = (skippedStepsList) => {
    const payload = {
      organizationType,
      organizationName: formData.organizationName,
      organizationSize: formData.organizationSize || undefined,
      industrySector: formData.industrySector || undefined,
      primaryUseCase: formData.primaryUseCase || undefined,
      complianceTimeline: formData.complianceTimeline || undefined,
      hasUsedCbamSoftwareBefore:
        formData.hasUsedCbamSoftwareBefore === 'Yes'
          ? true
          : formData.hasUsedCbamSoftwareBefore === 'No'
            ? false
            : undefined,
      annualVolume: formData.annualVolume || undefined,
      userJobRole: formData.userJobRole || undefined,
      otherJobRole: formData.otherJobRole || undefined,
      hearAboutUs: formData.hearAboutUs || undefined,
      otherHearAboutUs: formData.otherHearAboutUs || undefined,
      skippedSteps: skippedStepsList,
      // Free-tier association fallback: the invite is the mechanism of record
      // via the authenticated /invite/claim, but pass the opaque token through
      // so the backend can bind the plan + declarant connection at org-creation
      // time even if the claim raced. Never send the plan — the backend derives
      // it from the validated token.
      inviteToken: getFreeTierInvite()?.token || undefined,
      // Entry B (generic self-serve /free-supplier) carries no declarant token,
      // so signal the free-tier intent explicitly. The backend treats this as a
      // downgrade-only hint (assigns the restricted plan, never grants
      // entitlements) — see design plan §4.
      freeTier: hasFreeTierInvite() && !getFreeTierInvite()?.token ? true : undefined,
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    return payload;
  };

  const handleSubmit = (skippedStepsList = skippedSteps) => {
    setIsFinishing(true);
    const validationErrors = validateStep3(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Release the submitting state — leaving it set kept the Complete
      // setup button in its loading/disabled state after a validation
      // error, locking the user out of resubmitting.
      setIsFinishing(false);
      return;
    }

    setErrors({});
    const payload = buildPayload(skippedStepsList);
    completeOnboarding(payload);
  };

  const handleClose = () => {
    if (showCalendlyStep || showOperatorPdfStep) {
      activateOrgAndRedirect(pendingRedirectUrl || '/');
      return;
    }
    setExitConfirmOpen(true);
  };

  const handleCalendlyContinue = () => {
    // Operators get an extra optional PDF-download step before being dropped
    // into the platform. Everyone else continues straight through.
    if (organizationType === 'operator') {
      setShowCalendlyStep(false);
      setShowOperatorPdfStep(true);
      return;
    }
    activateOrgAndRedirect(pendingRedirectUrl);
  };

  const handleOperatorPdfContinue = () => {
    activateOrgAndRedirect(pendingRedirectUrl);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <WizardStep1
            formData={formData}
            updateField={updateField}
            onNext={handleNext}
            onSkip={handleSkip}
            errors={errors}
          />
        );
      case 2:
        return (
          <WizardStep2
            formData={formData}
            updateField={updateField}
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleSkip}
          />
        );
      case 3:
        return (
          <WizardStep3
            formData={formData}
            updateField={updateField}
            onSubmit={() => handleSubmit()}
            onBack={handleBack}
            onSkip={handleSkip}
            isSubmitting={isLoading || isFinishing || isPending || isFetching}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  const containerWidthClass = showCalendlyStep
    ? 'max-w-7xl'
    : showOperatorPdfStep
      ? 'max-w-4xl'
      : 'max-w-xl';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-primaryColor/5 to-transparent pointer-events-none"
      />

      <div className="relative flex-1 flex flex-col items-center justify-center px-4 py-8 sm:py-12">
        <img
          src={brand.logoUrl || logo}
          alt={t('onboarding:typeSelect.logoAlt', '{{brandName}} logo', { brandName: brand.name })}
          className="h-8 mb-6 opacity-90"
        />

        <div
          className={`${containerWidthClass} w-full bg-white border border-slate-200 rounded-2xl shadow-lg p-6 sm:p-8 relative`}
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-3 right-3 z-10 w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
            aria-label={t('common:actions.close', 'Close')}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
          <CgConfirmDialog
            open={exitConfirmOpen}
            onOpenChange={setExitConfirmOpen}
            title={t('common:actions.close', 'Close')}
            confirmLabel={t('common:actions.confirm', 'Confirm')}
            onConfirm={() => {
              setExitConfirmOpen(false);
              history.push('/');
            }}
          >
            {t('exitConfirm', 'Are you sure you want to exit? Your progress will be lost.')}
          </CgConfirmDialog>

          {showCalendlyStep ? (
            <CalendlyBookingStep onContinue={handleCalendlyContinue} />
          ) : showOperatorPdfStep ? (
            <OperatorOnboardingPdfStep onContinue={handleOperatorPdfContinue} />
          ) : (
            <>
              <WizardProgressBar
                currentStep={currentStep}
                totalSteps={3}
                labels={buildStepLabels(t)}
              />

              {errors.submit && <ErrorMessage message={errors.submit} />}

              {renderStep()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
